import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { Loader2, RefreshCw, Lock, FileText, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Gecontroleerde documentset-synchronisatie.
 *
 * Sprint jun 2026: na het wijzigen van gedeelde zendinggegevens kan de
 * medewerker de wijziging BEWUST doorzetten naar de overige documenten in
 * dezelfde set. Nooit blind alles overschrijven:
 *   - live renderende documenten staan er alleen informatief bij;
 *   - definitieve/financieel afgeronde documenten zijn geblokkeerd;
 *   - alleen aangevinkte documenten worden opnieuw uit de canonieke
 *     zendingdata gevuld.
 */
const MODE_META = {
  live: {
    title: "Automatisch actueel",
    icon: CheckCircle2,
    cls: "text-emerald-700 bg-emerald-50 border-emerald-200",
  },
  regenerate: {
    title: "Kan worden bijgewerkt",
    icon: RefreshCw,
    cls: "text-blue-800 bg-blue-50 border-blue-200",
  },
  immutable: {
    title: "Definitief — beschermd",
    icon: Lock,
    cls: "text-amber-800 bg-amber-50 border-amber-200",
  },
  manual: {
    title: "Handmatig bestand",
    icon: Upload,
    cls: "text-slate-700 bg-slate-50 border-slate-200",
  },
};

export default function DocumentSetSyncDialog({ open, shipmentId, onClose }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(async () => {
    if (!shipmentId) return;
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/shipping-labels/${shipmentId}/document-set`);
      setPlan(r.data);
      setSelected(new Set(
        (r.data?.targets || [])
          .filter((t) => t.sync_mode === "regenerate")
          .map((t) => `${t.target_type}:${t.target_id}`),
      ));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [shipmentId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggle = (key) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const apply = async () => {
    const targets = (plan?.targets || [])
      .filter((t) => selected.has(`${t.target_type}:${t.target_id}`))
      .map((t) => ({ target_type: t.target_type, target_id: t.target_id }));
    if (targets.length === 0) {
      toast.error("Selecteer minimaal 1 document.");
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.post(
        `/admin/shipping-labels/${shipmentId}/document-set/apply`, { targets },
      );
      const applied = r.data?.applied_count || 0;
      toast.success(`${applied} document${applied === 1 ? "" : "en"} bijgewerkt uit de zendingdata.`);
      const priceKept = (r.data?.applied || []).some((a) => a.price_preserved);
      if (priceKept) {
        toast.message("Ingevulde tarieven zijn behouden — controleer de bedragen.");
      }
      onClose(true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const groups = ["regenerate", "immutable", "live", "manual"];
  const targets = plan?.targets || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(false); }}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Deze wijziging ook toepassen op andere documenten</DialogTitle>
          <DialogDescription>
            Alleen documenten waarop de gewijzigde zendinggegevens van toepassing zijn.
            Documenten worden opnieuw uit de zendingdata gegenereerd — er wordt nooit
            tekst uit een ander document gekopieerd.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Documentset ophalen…
          </div>
        ) : (
          <div className="space-y-4" data-testid="document-set-sync-list">
            {groups.map((mode) => {
              const rows = targets.filter((t) => t.sync_mode === mode);
              if (rows.length === 0) return null;
              const meta = MODE_META[mode];
              const Icon = meta.icon;
              return (
                <div key={mode} className={`border ${meta.cls} rounded-md p-2`}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <Icon className="w-4 h-4" />
                    <span className="text-[11px] uppercase font-bold tracking-wide">
                      {meta.title}
                    </span>
                    <span className="ml-auto text-[11px] tabular-nums">{rows.length}</span>
                  </div>
                  <ul className="space-y-1">
                    {rows.map((t) => {
                      const key = `${t.target_type}:${t.target_id}`;
                      return (
                        <li key={key}
                            className="flex items-start gap-2 bg-white/70 p-1.5 rounded"
                            data-testid={`document-set-sync-row-${t.target_type}-${t.target_id}`}>
                          {mode === "regenerate" ? (
                            <input
                              type="checkbox"
                              checked={selected.has(key)}
                              onChange={() => toggle(key)}
                              className="mt-0.5"
                              data-testid={`document-set-sync-check-${t.target_type}-${t.target_id}`}
                            />
                          ) : (
                            <FileText className="w-3.5 h-3.5 mt-0.5 text-slate-400 flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="text-xs font-medium text-[#082c50] break-words">
                              {t.label}
                            </div>
                            <div className="text-[11px] text-slate-600 break-words">
                              {t.reason}
                            </div>
                            {t.fields?.length > 0 && (
                              <div className="text-[10px] text-slate-500 mt-0.5">
                                Velden: {t.fields.join(", ")}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
            {targets.length === 0 && (
              <div className="text-sm text-slate-600 italic" data-testid="document-set-sync-empty">
                Er zijn nog geen documenten voor deze zending.
              </div>
            )}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-slate-200">
          <Button
            variant="outline" onClick={() => onClose(false)}
            className="rounded-none text-xs"
            data-testid="document-set-sync-cancel"
          >
            Niet doorzetten
          </Button>
          <Button
            onClick={apply}
            disabled={busy || loading || (plan?.syncable_count || 0) === 0}
            className="rounded-none text-xs bg-[#0055FF] hover:bg-[#0044CC] text-white font-bold uppercase"
            data-testid="document-set-sync-apply"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Toepassen op selectie
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
